import React, { useEffect } from 'react';
import CalendarIcon from '@assets/images/calendar-icon-edu.svg';
import SunIcon from '@assets/images/sun-icon.svg';
import SecureIcon from '@assets/images/verified-icon.svg';
import StarIcon from '@assets/images/star-icon.svg';
import Image from 'next/image';
import closeIcon from '@assets/images/close-banner.svg';
import useEducationalModule from '@hooks/useEducationalModule';
import useOutsideClick from '@hooks/useOutsideClick';

const educationalModalContent = [
  {
    icon: CalendarIcon,
    text: 'Select a date and explore pool, spa, beach access and more, at 2,000+ top hotels for the day with no overnight stay required.',
  },
  {
    icon: SunIcon,
    text: 'Browse experiences for everyone across 250+ cities: kid-friendly waterpark excursions, poolside cabana celebrations, or spa passes for a moment of self-care.',
  },
  {
    icon: SecureIcon,
    text: 'Bookings can be cancelled for either a full refund or ResortPass Credit to use another day.',
  },
];

export default function EducationalModule() {
  const { educationalModalOpen, toggleEducationalModuleModal } = useEducationalModule();

  // Prevent background scrolling
  useEffect(() => {
    if (educationalModalOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [educationalModalOpen]);

  const [modalRef] = useOutsideClick(() => toggleEducationalModuleModal());

  return educationalModalOpen ? (
    <div
      className="bg-rp-white-faded justify-end items-end flex overflow-y-auto fixed inset-0 z-300 outline-none focus:outline-none d:items-center d:justify-center d:h-auto"
      onKeyDown={() => toggleEducationalModuleModal()}
      role="button"
      tabIndex={0}
      aria-label="Close"
    >
      <div
        className="relative w-auto mx-auto max-w-xl max-h-100dvh shadow-educational-modal rounded-lg"
        ref={modalRef}
      >
        <div className="border-0 relative flex flex-col w-full bg-white outline-none focus:outline-none">
          {/* header */}
          <div className="pt-10 px-6 d:px-12">
            <h2 className="font-semibold text-xl">Discover why people love ResortPass</h2>
            <div className="border rounded p-6 mt-5">
              <Image src={StarIcon} alt="5 Stars" />
              <Image src={StarIcon} alt="5 Stars" />
              <Image src={StarIcon} alt="5 Stars" />
              <Image src={StarIcon} alt="5 Stars" />
              <Image src={StarIcon} alt="5 Stars" />
              <p className="text-sm text-rp-neutral mt-3">
                “The opportunity to experience luxury hotels through ResortPass has made it easy to
                escape the everyday hustle and access premium amenities. Whether you’re planning a
                day of relaxation or a special occasion, this service truly delivers. I highly
                recommend it for anyone looking to elevate their daycation!”
              </p>
              <p className="text-sm text-black mt-3 font-semibold">Victoria K.</p>
              <p className="text-sm text-rp-text-secondary">Verified Guest</p>
            </div>
          </div>

          {/* body */}
          <div className="p-6 d:py-8 d:px-12 mb-20 d:mb-0">
            <h2 className="font-semibold text-xl">How ResortPass Works</h2>

            <div className="space-y-5 mt-6 d:mt-8">
              {educationalModalContent.map((item, i) => (
                <div key={`modal-content-${item.text}`} className="flex space-x-4">
                  <div className="flex-shrink-0 relative h-6 w-6 mt-1">
                    <Image src={item.icon} alt={`icon-${i}`} layout="fill" />
                  </div>

                  <div className="">
                    <p className="text-sm text-black">{item.text}</p>
                  </div>
                </div>
              ))}
              <div className="fixed bottom-0 w-full left-0 p-4 d:p-0 bg-rp-inverted shadow-bottom-nav d:relative">
                <button
                  type="button"
                  onClick={toggleEducationalModuleModal}
                  onKeyDown={toggleEducationalModuleModal}
                  className="bg-rp-primary w-full rounded-lg py-3 font-semibold text-sm text-white"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>

          <div
            onClick={toggleEducationalModuleModal}
            onKeyDown={toggleEducationalModuleModal}
            role="button"
            tabIndex={-1}
            aria-label="Close"
            className="absolute top-5 right-5 cursor-pointer"
          >
            <Image src={closeIcon} alt="Close Icon" />
          </div>
        </div>
      </div>
    </div>
  ) : null;
}
